import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, BadgeCheck, ShieldCheck } from "lucide-react";
import { useRoles } from "@/api/queries/roles";
import { useCreateRole } from "@/api/mutations/roles";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ListTableShell } from "@/components/ResourceShell";
import { SectionCard } from "@/components/SectionCard";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ApiError } from "@/api/client";

// #274: zwei Sektionen — frei anlegbare Fach-Rollen und die kuratierten System-Rollen
// (read-only: kein Anlegen/Löschen/Umbenennen, nur Mitglieder-Verwaltung im Detail).
export function RolesList() {
  const { data, isLoading, isError, error, refetch } = useRoles();
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);

  const rows = data ?? [];
  const fach = rows.filter((r) => r.kind === "fach");
  const system = rows.filter((r) => r.kind === "system");

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-4">
        <p className="text-[13px] text-text3">
          {fach.length} Fach-Rollen · {system.length} System-Rollen
        </p>
        <Button onClick={() => setCreateOpen(true)} data-testid="action-create-role">
          <Plus className="h-4 w-4" />
          Neue Fach-Rolle
        </Button>
      </div>

      <ListTableShell
        isLoading={isLoading && !data}
        isError={isError}
        errorMessage={(error as Error | undefined)?.message}
        isEmpty={!isLoading && fach.length === 0}
        emptyMessage="Noch keine Fach-Rollen"
        emptyHint="Fach-Rollen benennen Zuständigkeiten (z.B. 'Sachbearbeiter', 'Revisor') — sie verleihen KEINE Plattform-Rechte."
        onRetry={() => refetch()}
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Beschreibung</TableHead>
              <TableHead>Mitglieder</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {fach.map((r) => (
              <TableRow key={r.id} data-clickable="true" onClick={() => navigate(`/roles/${r.id}`)}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-border bg-surface2 text-text3">
                      <BadgeCheck className="h-4 w-4" />
                    </span>
                    <div className="font-semibold text-text">{r.name}</div>
                  </div>
                </TableCell>
                <TableCell>
                  <span className="text-[12.5px] text-text2">{r.description || "—"}</span>
                </TableCell>
                <TableCell>
                  <span className="text-[12.5px] text-text2">{r.memberCount}</span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ListTableShell>

      {/* Nur rendern, wenn Daten da sind — sonst stünde unter einer Fehlerkarte eine
          leere Tabelle, die „es gibt keine System-Rollen" suggeriert. */}
      {!isError && !(isLoading && !data) && (
      <SectionCard
        icon={<ShieldCheck className="h-[18px] w-[18px]" />}
        title="System-Rollen"
        description="Kuratiert und unveränderlich. Sie tragen Plattform-Rechte — nur Mitglieder sind verwaltbar."
        noPad
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Beschreibung</TableHead>
              <TableHead>Mitglieder</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {system.map((r) => (
              <TableRow key={r.id} data-clickable="true" onClick={() => navigate(`/roles/${r.id}`)}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <span className="font-semibold text-text mono">{r.name}</span>
                    <Badge variant="default">System</Badge>
                  </div>
                </TableCell>
                <TableCell>
                  <span className="text-[12.5px] text-text2">{r.description || "—"}</span>
                </TableCell>
                <TableCell>
                  <span className="text-[12.5px] text-text2">{r.memberCount}</span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </SectionCard>
      )}

      {createOpen && <CreateRoleDialog onClose={() => setCreateOpen(false)} onCreated={(id) => navigate(`/roles/${id}`)} />}
    </div>
  );
}

function CreateRoleDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const create = useCreateRole();

  function submit() {
    setErr(null);
    create.mutate(
      { name: name.trim(), description: description.trim() || undefined },
      {
        onSuccess: (role) => {
          toast.success(`Rolle "${role.name}" angelegt`);
          onClose();
          onCreated(role.id);
        },
        onError: (e) => {
          // 409 = Name vergeben, 400 = ungültiger Name. Verständlich statt roh.
          const status = e instanceof ApiError ? e.status : 0;
          setErr(
            status === 409
              ? "Eine Rolle mit diesem Namen existiert bereits (Groß-/Kleinschreibung zählt nicht)."
              : status === 400
                ? "Ungültiger Name: 1–64 Zeichen, Buchstaben/Ziffern/Leerzeichen/_/-, kein ':'."
                : (e as Error).message,
          );
        },
      },
    );
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Neue Fach-Rolle</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="role-name">Name</Label>
            <Input
              id="role-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="z.B. Revisor"
              data-testid="role-name-input"
            />
            <p className="text-[11.5px] text-text3">
              Der Name ist <strong>dauerhaft</strong> und kann später nicht geändert werden — Prozesse
              referenzieren ihn.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="role-description">Beschreibung (optional)</Label>
            <Textarea
              id="role-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>
          {err && <p className="text-[12.5px] text-danger" data-testid="role-create-error">{err}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={submit} disabled={!name.trim() || create.isPending} data-testid="role-create-submit">
            Anlegen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
